import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUser, clearAuthError } from './authSlice';
import { AppDispatch, RootState } from '../../app/store';
import toast from 'react-hot-toast';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const emailValueRef = React.useRef('');
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, error, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Clear any previous errors when the component mounts
    dispatch(clearAuthError());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && user) {
      toast.success(`Welcome back, ${user.name}!`);
      // Reset submitting state
      setIsSubmitting(false);
      // Redirect to dashboard
      navigate('/');
    } else if (error) {
      // Reset submitting state when there's an error
      setIsSubmitting(false);
      
      // Display error toast if login fails
      let errorMessage = 'Login failed';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
        errorMessage = (error as any).message;
      }
      toast.error(errorMessage);
      // Clear password on error but keep email
      console.log('Login failed - email should be preserved:', emailValueRef.current);
      setPassword('');
      // Focus password field for easy retry
      setTimeout(() => {
        passwordRef.current?.focus();
      }, 100);
      // Don't clear error here to prevent re-renders - let user retry
    }
  }, [isAuthenticated, error, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both email and password.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await dispatch(loginUser({ email, password }));
    } catch (error) {
      // Error is handled by the useEffect
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  // If already authenticated, just show a loading state while useEffect handles redirect
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Helper to get a displayable error message for inline display
  const getDisplayError = (err: unknown): string | null => {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') return (err as any).message;
    return err ? 'An unexpected error occurred.' : null;
  };

  const inlineError = getDisplayError(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Main Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 space-y-6">

          {/* Logo Section */}
          <div className="text-center">
            <div className="mx-auto w-20 h-20 bg-red-600 rounded-lg flex items-center justify-center mb-4">
              <div className="text-white font-bold text-2xl">
                {/* KFC-style logo placeholder - you can replace with actual logo */}
                <svg viewBox="0 0 24 24" className="w-12 h-12 fill-current">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
            </div>

            {/* Brand Title */}
            <h1 className="text-xl font-semibold text-red-600 dark:text-red-500 mb-8">
              Colefax Purchase Orders
            </h1>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">

            {/* Hidden dummy fields to prevent auto-fill */}
            <div style={{ display: 'none' }}>
              <input type="text" name="fakeusernameremembered" />
              <input type="password" name="fakepasswordremembered" />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                ref={emailRef}
                autoComplete="email"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                required
                value={email}
                onChange={(e) => {
                  console.log('Email changed to:', e.target.value);
                  setEmail(e.target.value);
                  emailValueRef.current = e.target.value;
                  // Clear error when user starts typing
                  if (error) {
                    dispatch(clearAuthError());
                  }
                }}
                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
                         placeholder-gray-400 dark:placeholder-gray-500
                         text-gray-900 dark:text-white
                         bg-white dark:bg-gray-700
                         focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500
                         transition-colors duration-200"
                placeholder="Enter your email"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                ref={passwordRef}
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Clear error when user starts typing
                  if (error) {
                    dispatch(clearAuthError());
                  }
                }}
                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm
                         placeholder-gray-400 dark:placeholder-gray-500
                         text-gray-900 dark:text-white
                         bg-white dark:bg-gray-700
                         focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500
                         transition-colors duration-200"
                placeholder="Enter your password"
              />
            </div>

            {/* Error Display */}
            {inlineError && !isAuthenticated && (
              <div className="text-red-600 dark:text-red-400 text-sm text-center py-2 bg-red-50 dark:bg-red-900/20 rounded-md">
                {inlineError}
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md
                       text-sm font-medium text-white
                       bg-red-600 hover:bg-red-700
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                       disabled:bg-red-400 dark:disabled:bg-red-500
                       disabled:cursor-not-allowed
                       transition-colors duration-200
                       shadow-lg hover:shadow-xl"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </div>
              ) : (
                'Log in'
              )}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-red-600 dark:text-red-500 hover:text-red-800 dark:hover:text-red-400
                         font-medium transition-colors duration-200 focus:outline-none focus:underline"
              >
                Forgot your password?
              </button>
            </div>
          </form>
        </div>

        {/* Footer Text */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            © 2024 Colefax. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
